/**
 * The signed-in customer's portal session (tracker item 45, phase 3): loads
 * GET /portal/me once for the shell and every page (company name, linked or
 * not, properties), and turns a failed call into the right screen.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAPIClient, IPortalApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { describePortalFailure, PortalFailure, PORTAL_LOGIN_PATH } from '../../utils/portal';
import { FailureBlock, LoadingBlock } from './PortalStates';

export interface PortalContextValue {
  me: types.PortalMe;
  /** Reload /portal/me (after a profile change) */
  refresh: () => Promise<void>;
  /** Pages hand their failures here: an expired session ends and goes back to the portal login */
  reportFailure: (failure: PortalFailure) => void;
}

const PortalContext = createContext<PortalContextValue | null>(null);

/** The portal API (the narrow contract), shared with the rest of the app's single client. */
export function usePortalApi(): IPortalApi {
  return useMemo(() => createAPIClient() as IPortalApi, []);
}

export function usePortal(): PortalContextValue {
  const value = useContext(PortalContext);
  if (!value) throw new Error('usePortal must be used within PortalProvider');
  return value;
}

export function PortalProvider({ children }: { children: React.ReactNode }) {
  const api = usePortalApi();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState<types.PortalMe | null>(null);
  const [failure, setFailure] = useState<PortalFailure | null>(null);
  const [loading, setLoading] = useState(true);
  const requestId = useRef(0);

  const endSession = useCallback(async () => {
    try {
      await logout();
    } finally {
      navigate(PORTAL_LOGIN_PATH, { replace: true });
    }
  }, [logout, navigate]);

  const reportFailure = useCallback((next: PortalFailure) => {
    if (next.kind === 'session') void endSession();
  }, [endSession]);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setFailure(null);
    try {
      const result = await api.getPortalMe();
      if (id !== requestId.current) return;
      setMe(result);
    } catch (error) {
      if (id !== requestId.current) return;
      const described = describePortalFailure(error, 'We could not load your account.');
      setFailure(described);
      reportFailure(described);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [api, reportFailure]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    try {
      setMe(await api.getPortalMe());
    } catch {
      /* the page that asked keeps what it has */
    }
  }, [api]);

  const value = useMemo(() => (me ? { me, refresh, reportFailure } : null), [me, refresh, reportFailure]);

  if (!value) {
    return (
      <div className="min-h-screen bg-gray-100 px-4 py-10">
        <div className="mx-auto max-w-md">
          {loading && <LoadingBlock label="Loading your account" />}
          {failure && !loading && <FailureBlock failure={failure} onRetry={() => void load()} />}
        </div>
      </div>
    );
  }
  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}
